import React, { useEffect, useState, useCallback } from 'react';
import { useT } from '../../context/TenantContext';
import BackDeskLayout from '../../components/layout/BackDeskLayout';
import MIcon from '../../components/common/MIcon';
import { fmtDate, fmtDateTime, duration, timeAgo } from '../../utils/dates';
import { Button } from '../../components/ui';

const STATUS_L  = { open:'Abierto', in_progress:'En progreso', escalated:'Escalado', resolved:'Resuelto', closed:'Resuelto' };
const STATUS_C  = { open:'#0085CA', in_progress:'#7C3AED', escalated:'#B45309', resolved:'#15803D', closed:'#15803D' };
const STATUS_BG = { open:'#E6F4FB', in_progress:'#EDE9FE', escalated:'#FFFBEB', resolved:'#F0FDF4', closed:'#F0FDF4' };
const PRIO_L    = { low:'Baja', medium:'Media', high:'Alta', critical:'Crítica' };
const PRIO_C    = { low:'#15803D', medium:'#B45309', high:'#0085CA', critical:'#B91C1C' };

function Stars({ rating }) {
  return (
    <span style={{fontSize:12,letterSpacing:1}}>
      {[1,2,3,4,5].map(n => (
        <span key={n} style={{color: n <= rating ? '#F59E0B' : '#E5E7EB'}}>★</span>
      ))}
    </span>
  );
}

function VipBadge() {
  return (
    <span style={{
      fontSize:9, fontWeight:700, padding:'2px 6px', borderRadius:6,
      background:'linear-gradient(135deg,#7C3AED,#A855F7)',
      color:'#fff', letterSpacing:.5, textTransform:'uppercase', flexShrink:0,
    }}>VIP</span>
  );
}

const fmt = fmtDate;

export default function Clients() {
  const { t } = useT();
  const token   = localStorage.getItem('hd_token');
  const headers = { Authorization: `Bearer ${token}` };

  const [clients,      setClients]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState('');
  const [clientModal,  setClientModal]  = useState(null);
  const [ticketModal,  setTicketModal]  = useState(null);
  const [loadingTk,    setLoadingTk]    = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [togglingVip,  setTogglingVip]  = useState(null); // key en proceso

  const load = useCallback(async () => {
    setLoading(true);
    const q = search ? `?search=${encodeURIComponent(search)}` : '';
    const data = await fetch(`/api/clients${q}`, { headers }).then(r => r.json()).catch(() => []);
    setClients(data);
    setLoading(false);
  }, [search]);

  useEffect(() => { load(); }, [load]);

  async function openClient(c) {
    setLoadingTk(true);
    setClientModal({ client: c, tickets: [] });
    const key = c.requester_email || c.requester;
    const data = await fetch(`/api/clients/${encodeURIComponent(key)}/tickets`, { headers })
      .then(r => r.json()).catch(() => []);
    setClientModal({ client: c, tickets: data });
    setLoadingTk(false);
  }

  async function openTicket(t) {
    setLoadingModal(true);
    setTicketModal({ ticket: t, comments: [] });
    const [full, comments] = await Promise.all([
      fetch(`/api/tickets/${t.id}`, { headers }).then(r => r.json()).catch(() => t),
      fetch(`/api/tickets/${t.id}/comments`, { headers }).then(r => r.json()).catch(() => []),
    ]);
    setTicketModal({ ticket: full, comments: comments.filter(c => !c.is_internal) });
    setLoadingModal(false);
  }

  async function toggleVip(c, e) {
    e.stopPropagation();
    const key = c.requester_email || c.requester;
    if (togglingVip === key) return;
    setTogglingVip(key);
    const method = c.is_vip ? 'DELETE' : 'POST';
    await fetch(`/api/clients/${encodeURIComponent(key)}/vip`, { method, headers }).catch(() => {});
    // Actualizar estado local sin re-fetch
    setClients(prev => prev.map(cl => {
      const k = cl.requester_email || cl.requester;
      return k === key ? { ...cl, is_vip: c.is_vip ? 0 : 1 } : cl;
    }));
    // Si el modal está abierto, actualizar el cliente ahí también
    if (clientModal?.client) {
      const k = clientModal.client.requester_email || clientModal.client.requester;
      if (k === key) setClientModal(prev => ({ ...prev, client: { ...prev.client, is_vip: c.is_vip ? 0 : 1 } }));
    }
    setTogglingVip(null);
  }

  return (
    <BackDeskLayout title="Clientes"
      actions={<span style={{fontSize:13,color:'var(--bd-muted)'}}>{clients.length} clientes</span>}>

      {/* Buscador */}
      <div style={{marginBottom:14}}>
        <input className="bd-search" placeholder="Buscar por nombre o email…"
          value={search} onChange={e => setSearch(e.target.value)}
          style={{width:320}}/>
      </div>

      {/* Tabla */}
      {loading
        ? <div className="bd-loading"><div className="bd-spinner-sm"/>Cargando…</div>
        : (
        <div style={{background:'#fff',border:'1px solid #E5E7EB',borderRadius:12,overflow:'hidden',boxShadow:'0 1px 3px rgba(0,0,0,.05)'}}>
          <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead>
              <tr style={{background:'#FAFAFA'}}>
                {['Cliente','Total tickets','Activos','Resueltos','Valoración media','Último ticket',''].map(h => (
                  <th key={h} style={{padding:'10px 16px',textAlign:'left',fontSize:10,fontWeight:600,
                    letterSpacing:.4,textTransform:'uppercase',color:'#9CA3AF',
                    borderBottom:'1px solid #F3F4F6'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {clients.length === 0 && (
                <tr><td colSpan={7} style={{padding:32,textAlign:'center',color:'#9CA3AF',fontSize:13}}>No se encontraron clientes</td></tr>
              )}
              {clients.map(c => {
                const key = c.requester_email || c.requester;
                const isVip = !!c.is_vip;
                return (
                  <tr key={key}
                    style={{borderBottom:'1px solid #F9FAFB',cursor:'default',
                      background: isVip ? 'linear-gradient(90deg,#FAF5FF 0%,#fff 60%)' : ''}}>
                    {/* Cliente */}
                    <td style={{padding:'12px 16px'}}>
                      <div style={{display:'flex',alignItems:'center',gap:10}}>
                        <div style={{
                          width:36,height:36,borderRadius:'50%',flexShrink:0,display:'flex',
                          alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:700,
                          background: isVip ? 'linear-gradient(135deg,#7C3AED,#A855F7)' : '#E6F4FB',
                          color: isVip ? '#fff' : '#0085CA',
                        }}>
                          {c.requester?.charAt(0)?.toUpperCase()}
                        </div>
                        <div>
                          <div style={{display:'flex',alignItems:'center',gap:6}}>
                            <span style={{fontSize:13,fontWeight:600,color:'#111827'}}>{c.requester}</span>
                            {isVip && <VipBadge/>}
                          </div>
                          {c.requester_email && <div style={{fontSize:11,color:'#9CA3AF'}}>{c.requester_email}</div>}
                        </div>
                      </div>
                    </td>
                    {/* Total */}
                    <td style={{padding:'12px 16px'}}>
                      <span style={{fontSize:14,fontWeight:700,color:'#374151'}}>{c.total}</span>
                    </td>
                    {/* Activos */}
                    <td style={{padding:'12px 16px'}}>
                      <span style={{fontSize:14,fontWeight:700,color: c.active > 0 ? '#7C3AED' : '#9CA3AF'}}>{c.active}</span>
                    </td>
                    {/* Resueltos */}
                    <td style={{padding:'12px 16px'}}>
                      <span style={{fontSize:14,fontWeight:700,color:'#15803D'}}>{c.resolved}</span>
                    </td>
                    {/* Valoración */}
                    <td style={{padding:'12px 16px'}}>
                      {c.avg_rating > 0
                        ? <div><Stars rating={Math.round(c.avg_rating)}/><span style={{fontSize:11,color:'#9CA3AF',marginLeft:4}}>{c.avg_rating} ({c.rating_count})</span></div>
                        : <span style={{fontSize:12,color:'#D1D5DB'}}>—</span>}
                    </td>
                    {/* Último ticket */}
                    <td style={{padding:'12px 16px',fontSize:12,color:'#6B7280'}}>{fmt(c.last_ticket_at)}</td>
                    {/* Acciones */}
                    <td style={{padding:'12px 16px'}}>
                      <div style={{display:'flex',alignItems:'center',gap:8}}>
                        <button
                          className="bd-btn bd-btn-ghost"
                          style={{
                            height:30, fontSize:11, gap:4,
                            ...(isVip ? {
                              background:'linear-gradient(135deg,#7C3AED,#A855F7)',
                              color:'#fff', border:'none',
                            } : {}),
                          }}
                          onClick={e => toggleVip(c, e)}
                          title={isVip ? 'Quitar VIP' : 'Marcar como VIP'}
                          disabled={togglingVip === key}>
                          <MIcon name="workspace_premium" size={14}/>
                          {isVip ? 'VIP' : 'Hacer VIP'}
                        </button>
                        <Button variant="ghost" size="sm" style={{height:30}}
                          onClick={() => openClient(c)}>
                          Ver tickets
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Modal: tickets del cliente ── */}
      {clientModal && (
        <div style={{position:'fixed',inset:0,zIndex:1000,background:'rgba(15,30,60,.45)',display:'flex',alignItems:'center',justifyContent:'center',padding:20}}
          onClick={e => { if (e.target===e.currentTarget) setClientModal(null); }}>
          <div style={{background:'#fff',borderRadius:16,width:'100%',maxWidth:700,maxHeight:'88vh',display:'flex',flexDirection:'column',boxShadow:'0 12px 48px rgba(0,0,0,.18)'}}>
            {/* Header */}
            <div style={{padding:'20px 24px',borderBottom:'1px solid #F3F4F6',display:'flex',alignItems:'center',justifyContent:'space-between',gap:12}}>
              <div style={{display:'flex',alignItems:'center',gap:12}}>
                <div style={{
                  width:40,height:40,borderRadius:'50%',flexShrink:0,
                  display:'flex',alignItems:'center',justifyContent:'center',fontSize:15,fontWeight:700,
                  background: clientModal.client.is_vip ? 'linear-gradient(135deg,#7C3AED,#A855F7)' : '#E6F4FB',
                  color: clientModal.client.is_vip ? '#fff' : '#0085CA',
                }}>
                  {clientModal.client.requester?.charAt(0)?.toUpperCase()}
                </div>
                <div>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    <span style={{fontSize:15,fontWeight:700,color:'#111827'}}>{clientModal.client.requester}</span>
                    {!!clientModal.client.is_vip && <VipBadge/>}
                  </div>
                  {clientModal.client.requester_email && <div style={{fontSize:12,color:'#6B7280'}}>{clientModal.client.requester_email}</div>}
                </div>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:16}}>
                {[
                  { label:'Total', value: clientModal.client.total, color:'#374151' },
                  { label:'Activos', value: clientModal.client.active, color: clientModal.client.active>0?'#7C3AED':'#9CA3AF' },
                  { label:'Resueltos', value: clientModal.client.resolved, color:'#15803D' },
                ].map(k => (
                  <div key={k.label} style={{textAlign:'center'}}>
                    <div style={{fontSize:18,fontWeight:700,color:k.color}}>{k.value}</div>
                    <div style={{fontSize:10,color:'#9CA3AF',textTransform:'uppercase',letterSpacing:.4}}>{k.label}</div>
                  </div>
                ))}
                {clientModal.client.avg_rating > 0 && (
                  <div style={{textAlign:'center'}}>
                    <Stars rating={Math.round(clientModal.client.avg_rating)}/>
                    <div style={{fontSize:10,color:'#9CA3AF',marginTop:2}}>{clientModal.client.avg_rating} media</div>
                  </div>
                )}
                <button onClick={() => setClientModal(null)}
                  style={{background:'none',border:'none',cursor:'pointer',color:'#9CA3AF',padding:4,display:'flex'}}>
                  <MIcon name="close" size={20}/>
                </button>
              </div>
            </div>
            {/* Lista tickets */}
            <div style={{overflowY:'auto',flex:1}}>
              {loadingTk && <div style={{padding:32,textAlign:'center',color:'#9CA3AF',fontSize:13}}>Cargando…</div>}
              {!loadingTk && clientModal.tickets.length === 0 && (
                <div style={{padding:32,textAlign:'center',color:'#9CA3AF',fontSize:13}}>Sin tickets</div>
              )}
              {!loadingTk && (
                <table style={{width:'100%',borderCollapse:'collapse'}}>
                  <thead>
                    <tr style={{background:'#FAFAFA'}}>
                      {['Referencia','Título','Estado','Prioridad','Agente','Valoración','Fecha'].map(h => (
                        <th key={h} style={{padding:'8px 16px',textAlign:'left',fontSize:10,fontWeight:600,
                          letterSpacing:.4,textTransform:'uppercase',color:'#9CA3AF',borderBottom:'1px solid #F3F4F6'}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {clientModal.tickets.map(t => (
                      <tr key={t.id} onClick={() => openTicket(t)}
                        style={{borderBottom:'1px solid #F9FAFB',cursor:'pointer',transition:'background .1s'}}
                        onMouseEnter={e => e.currentTarget.style.background='#F9FAFB'}
                        onMouseLeave={e => e.currentTarget.style.background=''}>
                        <td style={{padding:'10px 16px',fontFamily:'monospace',fontSize:11,color:'#0085CA',fontWeight:600}}>{t.ref}</td>
                        <td style={{padding:'10px 16px',fontSize:12,color:'#111827',fontWeight:500,maxWidth:200}}>
                          <div style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t.title}</div>
                        </td>
                        <td style={{padding:'10px 16px'}}>
                          <span style={{fontSize:10,fontWeight:600,padding:'2px 7px',borderRadius:10,
                            background:STATUS_BG[t.status],color:STATUS_C[t.status]}}>
                            {STATUS_L[t.status]}
                          </span>
                        </td>
                        <td style={{padding:'10px 16px',fontSize:11,fontWeight:600,color:PRIO_C[t.priority]}}>{PRIO_L[t.priority]}</td>
                        <td style={{padding:'10px 16px',fontSize:12,color:'#6B7280'}}>{t.assignee_name || <span style={{color:'#D1D5DB'}}>—</span>}</td>
                        <td style={{padding:'10px 16px'}}>{t.survey_rating ? <Stars rating={t.survey_rating}/> : <span style={{color:'#D1D5DB',fontSize:12}}>—</span>}</td>
                        <td style={{padding:'10px 16px',fontSize:11,color:'#9CA3AF',whiteSpace:'nowrap'}}>{fmt(t.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: detalle ticket ── */}
      {ticketModal && (
        <div style={{position:'fixed',inset:0,zIndex:1100,background:'rgba(15,30,60,.5)',display:'flex',alignItems:'center',justifyContent:'center',padding:20}}
          onClick={e => { if (e.target===e.currentTarget) setTicketModal(null); }}>
          <div style={{background:'#fff',borderRadius:16,width:'100%',maxWidth:580,maxHeight:'85vh',overflowY:'auto',boxShadow:'0 12px 48px rgba(0,0,0,.2)'}}>
            <div style={{padding:'20px 24px 16px',borderBottom:'1px solid #F3F4F6',display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:12}}>
              <div style={{flex:1}}>
                <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6,flexWrap:'wrap'}}>
                  <span style={{fontSize:11,fontWeight:600,padding:'2px 8px',borderRadius:10,
                    background:STATUS_BG[ticketModal.ticket.status],color:STATUS_C[ticketModal.ticket.status]}}>
                    {STATUS_L[ticketModal.ticket.status]}
                  </span>
                  <span style={{fontSize:11,fontWeight:600,color:PRIO_C[ticketModal.ticket.priority]}}>{PRIO_L[ticketModal.ticket.priority]}</span>
                  <span style={{fontFamily:'monospace',fontSize:11,color:'#0085CA',fontWeight:600}}>{ticketModal.ticket.ref}</span>
                  <span style={{fontSize:11,color:'#9CA3AF',marginLeft:'auto'}}>{fmt(ticketModal.ticket.created_at)}</span>
                </div>
                <div style={{fontSize:16,fontWeight:700,color:'#111827',lineHeight:1.4}}>{ticketModal.ticket.title}</div>
              </div>
              <button onClick={() => setTicketModal(null)}
                style={{background:'none',border:'none',cursor:'pointer',color:'#9CA3AF',padding:4,flexShrink:0,display:'flex'}}>
                <MIcon name="close" size={20}/>
              </button>
            </div>
            {loadingModal
              ? <div style={{padding:32,textAlign:'center',color:'#9CA3AF',fontSize:13}}>Cargando…</div>
              : (
              <div style={{padding:'16px 24px 24px',display:'flex',flexDirection:'column',gap:14}}>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                  {[
                    { icon:'person',        label:'Solicitante', value: ticketModal.ticket.requester },
                    { icon:'mail',          label:'Email',       value: ticketModal.ticket.requester_email },
                    { icon:'support_agent', label:'Asignado a',  value: ticketModal.ticket.assignee_name || 'Sin asignar' },
                    { icon:'schedule',      label:'Creado',      value: fmt(ticketModal.ticket.created_at) },
                    ticketModal.ticket.first_response_at && { icon:'reply', label:'1ª respuesta', value: duration(ticketModal.ticket.created_at, ticketModal.ticket.first_response_at), color:'#7C3AED' },
                    ticketModal.ticket.resolved_at && { icon:'timer', label:'Tiempo resolución', value: duration(ticketModal.ticket.created_at, ticketModal.ticket.resolved_at), color:'#15803D' },
                    ticketModal.ticket.resolved_at && { icon:'check_circle', label:'Resuelto', value: fmt(ticketModal.ticket.resolved_at), color:'#15803D' },
                  ].filter(Boolean).map(row => (
                    <div key={row.label} style={{display:'flex',alignItems:'flex-start',gap:8}}>
                      <MIcon name={row.icon} size={14} style={{color:'#C4C9D4',marginTop:2,flexShrink:0}}/>
                      <div>
                        <div style={{fontSize:10,fontWeight:600,textTransform:'uppercase',letterSpacing:.5,color:'#9CA3AF',marginBottom:1}}>{row.label}</div>
                        <div style={{fontSize:13,color:row.color||'#111827',fontWeight:500}}>{row.value||'—'}</div>
                      </div>
                    </div>
                  ))}
                </div>
                {ticketModal.ticket.description && (
                  <div>
                    <div style={{fontSize:10,fontWeight:600,textTransform:'uppercase',letterSpacing:.5,color:'#9CA3AF',marginBottom:6}}>Descripción</div>
                    <p style={{fontSize:13,color:'#4B5563',lineHeight:1.7,margin:0,background:'#F9FAFB',borderRadius:8,padding:'10px 14px',whiteSpace:'pre-wrap'}}>{ticketModal.ticket.description}</p>
                  </div>
                )}
                {ticketModal.ticket.survey_rating && (
                  <div style={{background:'#FFFBEB',border:'1px solid #FDE68A',borderRadius:8,padding:'10px 14px'}}>
                    <div style={{fontSize:10,fontWeight:600,textTransform:'uppercase',letterSpacing:.5,color:'#B45309',marginBottom:6}}>Valoración</div>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <Stars rating={ticketModal.ticket.survey_rating}/>
                      <span style={{fontSize:13,fontWeight:600,color:'#374151'}}>{ticketModal.ticket.survey_rating}/5</span>
                    </div>
                    {ticketModal.ticket.survey_comment && (
                      <p style={{fontSize:13,color:'#6B7280',fontStyle:'italic',margin:'6px 0 0'}}>"{ticketModal.ticket.survey_comment}"</p>
                    )}
                  </div>
                )}
                {ticketModal.comments.length > 0 && (
                  <div>
                    <div style={{fontSize:10,fontWeight:600,textTransform:'uppercase',letterSpacing:.5,color:'#9CA3AF',marginBottom:8}}>Comentarios ({ticketModal.comments.length})</div>
                    <div style={{display:'flex',flexDirection:'column',gap:8}}>
                      {ticketModal.comments.map(c => (
                        <div key={c.id} style={{padding:'10px 12px',borderRadius:8,background:'#F8FAFF',borderLeft:'3px solid #BFDBFE'}}>
                          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                            <div style={{width:22,height:22,borderRadius:'50%',background:'#0085CA',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,fontWeight:700,flexShrink:0}}>
                              {c.author_name?.charAt(0)?.toUpperCase()}
                            </div>
                            <span style={{fontSize:12,fontWeight:600,color:'#111827'}}>{c.author_name}</span>
                            <span style={{fontSize:11,color:'#9CA3AF',marginLeft:'auto'}}>{timeAgo(c.created_at)}</span>
                          </div>
                          <div style={{fontSize:13,color:'#374151',lineHeight:1.6,paddingLeft:30,whiteSpace:'pre-wrap'}}>{c.body}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </BackDeskLayout>
  );
}
